import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import React, { useCallback } from 'react';
import {
  MultiDropdown,
  useMultiDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { withLocalePrefix } from 'shared/utils/translation';
import Api from '../../lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { ItemWithOptions, PageName } from '../../shared/types';
import Checkbox from '@grnhse/seedling/lib/birch/components/checkbox';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';

type Props = {
  apiClient: Api;
  pageName: PageName;
};

const RejectionReasonsDropdown = ({ apiClient, pageName }: Props) => {
  const t = withLocalePrefix(`talent_${pageName}.labels`);

  const queryClient = useQueryClient();
  const { watch, setValue } = useFormContext();
  const selectedRejectionReasons: Item[] = watch('rejection_reasons');
  const excludeByRejectionReasons: boolean = watch(
    'exclude_by_rejection_reasons'
  );

  const toNestedDropdown = (items: ItemWithOptions[]): Item[] => {
    return items.map((item) => {
      return {
        label: item.label,
        value: item.value,
        disabled: true,
        items: item.options,
      };
    });
  };

  const fetchRejectionReasons = useCallback(
    async (term: string) => {
      const queryKey = ['rejectionReasons', term];

      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);

      if (cachedData) {
        return { items: toNestedDropdown(cachedData) };
      }

      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: () => apiClient.fetchRejectionReasons(term),
      });

      return { items: toNestedDropdown(items) };
    },
    [apiClient, queryClient]
  );

  const onRejectionReasonsChange = (e: { selectedItems: Array<Item> }) => {
    setValue('rejection_reasons', e.selectedItems, { shouldDirty: true });
  };

  const rejectionReasonsDropdownProps = useMultiDropdown({
    fetchItems: fetchRejectionReasons,
    onSelectedItemsChange: onRejectionReasonsChange,
    selectedItems: selectedRejectionReasons,
    preventLoadOnMount: true,
  });

  return (
    <Stack spacing={0.5}>
      <MultiDropdown dropdownProps={rejectionReasonsDropdownProps}>
        <MultiDropdown.Field
          data-provides={`talent-${pageName}-rejection-reasons-dropdown`}
          label={t('rejection_reasons')}
        />
        <MultiDropdown.Flyout />
      </MultiDropdown>
      <Grid sx={{ marginLeft: 2 }}>
        <Checkbox
          data-provides={`talent-${pageName}-exclude-rejection-reasons-checkbox`}
          checked={excludeByRejectionReasons}
          onChange={(checked) =>
            setValue('exclude_by_rejection_reasons', checked)
          }
          label={t('exclude_rejection_reasons')}
          value={'exclude_by_rejection_reasons'}
        />
      </Grid>
    </Stack>
  );
};

export default RejectionReasonsDropdown;
